import {
  type Block,
  type ChannelId,
  links_of,
  type MarkupNames,
  type Seq,
  type UserId,
} from '@maru/protocol'
import { MessagesSquare } from 'lucide-react'
import { lazy, memo, type MouseEvent as ReactMouseEvent, Suspense, useState } from 'react'
import type { FileMap } from './attachment_view.tsx'
import { BodyView, type EmojiLook, type GroupLook } from './body_view.tsx'
import { fmt_relative, fmt_time } from './format.ts'
import { type Locale, useLocale, useT } from './i18n.ts'
import { Button } from './kit/button.tsx'
import { cx } from './kit/cx.ts'
import { Avatar, Badge } from './kit/display.tsx'
import { useLongPress } from './kit/long_press.ts'
import { type CardMap, LinkCards } from './link_card.tsx'
import type { PollLook } from './poll_card.tsx'
import { ReactionBar, type Tally } from './reaction_bar.tsx'
import type { ShortcutItem } from './shortcut_menu.tsx'

// 줄 위의 도구 줄(빠른 반응·반응 추가·답글·나중에·더 보기)은 손이 닿아야 보이는 것이라 첫 화면 청크에
// 싣지 않는다 — 메뉴 키보드·자리 잡기·아이콘들이 따라온다. 첫 줄을 그리는 순간 받기 시작하므로 손이 닿을
// 때쯤엔 와 있다.
const Hands = lazy(() => import('./message_more.tsx').then((m) => ({ default: m.Hands })))
// 제자리 고치기 칸도 고칠 때만 받는다(첫 화면 번들 — 3차 F68 회차에 상한을 넘었다).
const EditForm = lazy(() => import('./edit_form.tsx').then((m) => ({ default: m.EditForm })))
// 알약 줄 끝의 "반응 추가" 도 같다 — 이모지 표와 찾기 칸은 손이 닿아야 쓴다.
const ReactPick = lazy(() => import('./emoji_pick.tsx').then((m) => ({ default: m.ReactPick })))
// 투표 카드도 투표가 달린 줄이 있을 때만 받는다.
const PollCard = lazy(() => import('./poll_card.tsx').then((m) => ({ default: m.PollCard })))

export interface Line {
  seq: Seq
  author: UserId
  body: Block[]
  written_at: string
  edited_at: string | null
  deleted_at: string | null
  /** 스레드 답글인데 채널에도 보냈다. 본류에서 "스레드 답글" 표를 단다. */
  broadcast?: boolean
  /** 답글이면 뿌리의 seq. 본류의 "스레드 답글" 표가 그 스레드를 연다. */
  parent?: Seq | null
}

/** 줄에 붙는 손. 고치기·지우기는 내 줄에만, 반응·스레드는 모든 줄에. */
export interface RowActions {
  on_edit?(seq: Seq, blocks: Block[]): Promise<void>
  on_delete?(seq: Seq): Promise<void>
  /** 내 줄의 링크 카드 하나를 뗀다. 고치기처럼 내 줄에만 뜬다. */
  on_hide_card?(seq: Seq, url: string): void
  on_react?(seq: Seq, emoji: string): void
  /** 투표에 내 표를 바꾼다(번호는 0부터). 없으면 투표는 결과만 보인다. */
  on_poll_vote?(seq: Seq, choices: number[]): void
  on_poll_unvote?(seq: Seq): void
  /** 만든 사람이 투표를 닫는다. */
  on_poll_close?(seq: Seq): void
  /** 스레드를 연다. 채널을 주면 그 채널의 것이다(스레드 모아 보기, 3차 F185). */
  on_thread?(seq: Seq, channel?: ChannelId): void
  /** 핀을 꽂거나 뺀다. 핀은 채널의 것이라 누구나 건드린다. */
  on_pin?(seq: Seq, on: boolean): void
  /** 나중에 보기에 담거나 뺀다. 이건 내 것이라 남에게 안 보인다. */
  on_save?(seq: Seq, on: boolean): void
  /** 이 줄을 나중에 보기에 담고 그때 알려 달라고 한다(`at` 은 절대 시각). */
  on_remind?(seq: Seq, at: string): void
  /** 이 줄부터 안 읽음으로 둔다. */
  on_unread?(seq: Seq): void
  /** 이 줄로 오는 영구 링크를 복사한다. */
  on_link?(seq: Seq): void
  /** 이 줄을 다른 채널로 전달한다. */
  on_forward?(seq: Seq): void
  /** 이름을 눌렀다. 없으면 이름은 그냥 글자다. */
  on_who?(user: UserId): void
  /** 앱의 단추를 눌렀다. 없으면 단추는 잠겨 있다. */
  on_action?(seq: Seq, block: number, action: string, value?: string): Promise<void>
  /** 앱의 글 단축들(슬랙 message shortcuts). `on_shortcut` 과 함께 있을 때만 뜬다. */
  shortcuts?: readonly ShortcutItem[]
  /** 조직의 커스텀 이모지 이름들. 반응 고르기 창의 맨 위에 선다. */
  emoji_names?: readonly string[]
  /** 이 줄로 글 단축을 부른다. 던지면 메뉴 곁에 까닭을 적는다. */
  on_shortcut?(seq: Seq, key: string): Promise<void>
}

export interface MessageRowProps extends RowActions {
  line: Line
  /** 바로 위 줄과 같은 사람이 이어 쓴 것. 이름과 시각을 다시 그리지 않는다. */
  run_on: boolean
  name_of(user: UserId): string | undefined
  /** 봇(웹훅·워크플로·앱)인가. 이름 곁에 구성원 판과 같은 "봇" 표식을 단다 — 사람이 쓴 글로 읽히지 않게(3차 F85). */
  bot_of?: ((user: UserId) => boolean) | undefined
  /** 그룹 번호 → 이름과 "내가 그 안에 있는가". 없으면 번호로 그린다. */
  group_of?: GroupLook | undefined
  /** 커스텀 이모지 이름 → 그림 주소. */
  emoji_of?: EmojiLook | undefined
  me: UserId
  /** 검색에서 뛰어온 줄. 잠깐 도드라지게 그린다. */
  focused?: boolean | undefined
  /** 이 줄에 붙은 반응. 없으면 알약을 그리지 않는다. */
  tallies?: readonly Tally[] | undefined
  /** 이 줄에 달린 투표. 없으면 그리지 않는다. */
  poll?: PollLook | null | undefined
  /** 이 줄에 달린 답글 수. 0 이면 "답글" 단추를 안 그린다(열기는 손에서 한다). */
  reply_count?: number | undefined
  /** 마지막 답글 시각(ISO). 있으면 답글 단추 옆에 "마지막 답글 5분 전". */
  reply_last?: string | undefined
  files?: FileMap | undefined
  cards?: CardMap | undefined
  /** 이 줄에 핀이 꽂혀 있나. */
  pinned?: boolean | undefined
  /** 내가 담아 둔 줄인가. */
  saved?: boolean | undefined
  /** 바뀔 때마다 고치기 칸을 연다(입력칸에서 ↑). 이 줄이 아니면 비운다. */
  edit_ask?: number | undefined
}

/**
 * 줄 하나. **`memo` 로 감싼다** — 새 줄 하나가 올 때 이미 그린 수백 줄이 다시 그려지지 않게.
 * React Compiler 는 컴포넌트 안을 기억하지만, 목록의 `map` 에서 줄마다 요소를 새로 만드는 것은
 * 막지 못한다. 넘기는 손·지도는 부르는 쪽(컴파일된 Room)이 같은 것을 준다.
 */
export const MessageRow = memo(function MessageRow(props: MessageRowProps) {
  const { line, run_on, name_of, me, on_edit, on_delete, on_who } = props
  const t = useT()
  const locale = useLocale()
  // 고치는 중인가. 칸의 글은 고치기 칸(늦은 청크)이 쥔다 — 본문을 표기로 펴는 코드를 첫 화면에 싣지 않는다.
  const [editing, set_editing] = useState(false)
  // 고치기 칸은 호출을 이름으로 편다 — 번호(`@3`)로 펴면 저장 한 번에 호출이 글자가 된다(3차 F200).
  const names: MarkupNames = {
    user: name_of,
    group: (g) => props.group_of?.(g)?.handle,
  }
  // ↑ 요청은 **번호가 바뀔 때만** 편다. 본문에 걸면 줄을 새로 받아 올 때마다(재동기화) 취소한
  // 편집이 다시 열리고, 줄이 다시 그려질 때 첫 값으로 열어도 같다(3차 F122).
  const ask = props.edit_ask
  const [asked, set_asked] = useState(ask)
  if (ask !== asked) {
    set_asked(ask)
    if (ask !== undefined) set_editing(true)
  }
  // 손가락에는 지나가기가 없어 손 막대에 닿을 길이 없었다. **길게 누르면** 막대가 서고(`held`) 더 보기
  // 메뉴까지 뜬다 — 오른쪽 클릭의 자리다. 짧은 탭은 막대를 세우지 않는다: 처음엔 탭이 막대를 토글했는데,
  // 대화를 읽으며 화면을 짚을 때마다 이모지 줄이 떠서 "누를 때마다 이모지 창이 뜬다" 는 이슈가 됐다(슬랙도
  // 탭에는 아무 일도 없다). 탭은 서 있는 막대를 **내리기만** 한다. 단추·링크·입력칸에서 시작한 누름은 그것들 것이다.
  const [held, set_held] = useState(false)
  const hold = useLongPress<HTMLDivElement>(
    () => {
      set_held(true)
      // 안드로이드 WebView 는 길게 누르면 글자까지 고른다(에뮬레이터 실측 — "10월" 이 파랗게 섰다). 막대와 글자
      // 선택이 같이 서면 어느 쪽을 누른 건지 헷갈리니 거둔다 — contextmenu 쪽은 `useLongPress` 가 거둔다.
      window.getSelection()?.removeAllRanges()
    },
    {
      tap: () => set_held(false),
      skip: 'button, a, input, textarea, [popover], dialog',
      // 더 보기 메뉴는 손가락을 뗀 뒤에 연다(`release` 의 까닭은 `kit/long_press.ts`). 막대가 선 뒤라야
      // `display: none` 이던 단추에 붙인 메뉴가 자리를 잡는다.
      release: (row) => row.querySelector<HTMLButtonElement>('.hands .more')?.click(),
    },
  )

  // 길게 누르기 손잡이에 마우스 오른쪽 클릭을 얹는다.
  const hands = {
    ...hold,
    onContextMenu: (e: ReactMouseEvent<HTMLDivElement>) => {
      hold.onContextMenu(e)
      if (e.defaultPrevented) return
      // 마우스 오른쪽 클릭은 더 보기 메뉴(리마인더·고정·링크…)를 연다. 글자를 골라 둔 채거나 링크·입력칸
      // 위에서 누르면 브라우저 메뉴(복사·링크 열기)가 그 자리의 일이라 그대로 둔다.
      if (!row_menu_wanted(e.target, window.getSelection()?.toString() ?? '')) return
      const more = e.currentTarget.querySelector<HTMLButtonElement>('.hands .more')
      if (more === null) return
      e.preventDefault()
      set_held(true)
      more.click()
    },
  }

  if (line.deleted_at !== null) {
    // 지운 줄도 자리를 남긴다. 통째로 사라지면 대화가 맥락 없이 튄다. 글 앞에 쓴 사람을 둔다 — 없으면 앞사람
    // 글의 이어 쓰기로 읽혀, 박도윤이 지운 줄이 김마루가 지운 것처럼 보였다(3차 F256).
    return (
      <div className="MessageRow deleted">
        <span className="gutter" />
        <span className="tomb">
          <span className="tomb-who">{who_of(name_of, line.author)}</span>{' '}
          {t('이 메시지는 삭제되었어요.')}
        </span>
      </div>
    )
  }

  const name = who_of(name_of, line.author)
  // 손은 **내 줄**에만 있다. 남의 줄을 고칠 권한은 서버가 어차피 거절하지만,
  // 안 되는 버튼을 보여 주고 404 를 돌려주는 것은 사람을 헷갈리게 한다.
  const mine = line.author === me && (on_edit !== undefined || on_delete !== undefined)

  // `null` 은 안 바뀐 저장이다 — 보내지 않고 닫는다. 빈 본문은 닫지도 않는다(지우기는 따로 있다).
  async function save(blocks: Block[] | null) {
    if (blocks?.length === 0) return
    if (blocks !== null) await on_edit?.(line.seq, blocks)
    set_editing(false)
  }

  return (
    <div
      className={cx(
        'MessageRow',
        run_on && 'run-on',
        props.pinned === true && 'pinned',
        props.focused === true && 'focused',
        held && 'held',
      )}
      onPointerEnter={(e) => hands_room(e.currentTarget)}
      {...hands}
    >
      {/* 왼쪽 여백: 덩어리 첫 줄은 얼굴, 이어 쓴 줄은 손이 닿을 때만 시각. 이름 글자가 곁에 있어 얼굴은 읽히지 않는다. */}
      <span className="gutter">
        {run_on ? (
          <time dateTime={line.written_at} title={clock(line.written_at, locale)}>
            {/* 칸이 36px 라 오전·오후를 뗀다(`3:05`). 온전한 시각은 풍선이 든다. */}
            {clock(line.written_at, locale).replace(/[^\d:]/g, '')}
          </time>
        ) : (
          <Avatar name={name} user={line.author} size="lg" decorative />
        )}
      </span>
      <div className="main">
        {/* 핀은 채널의 것이라 모두에게 보인다. 줄에 표가 없으면 핀 목록을 열기 전엔 아무도 모른다. */}
        {props.pinned === true && (
          <div className="pin-note">
            <span aria-hidden>📌</span>
            {t('채널에 고정됨')}
          </div>
        )}
        {/* 나중에 보기는 내 것이라 나에게만 보인다. 표가 없으면 담았는지 손을 올려 봐야 안다(슬랙과 같다). */}
        {props.saved === true && (
          <div className="pin-note">
            <span aria-hidden>🔖</span>
            {t('나중에 보기에 저장됨')}
          </div>
        )}
        {!run_on && (
          <Head
            name={name}
            bot={props.bot_of?.(line.author) === true}
            written_at={line.written_at}
            {...(on_who === undefined ? {} : { press: () => on_who(line.author) })}
          />
        )}
        {!editing ? (
          <Shown {...props} begin_edit={() => set_editing(true)} mine={mine} />
        ) : (
          <Suspense fallback={null}>
            <EditForm
              body={line.body}
              names={names}
              save={(blocks) => void save(blocks)}
              cancel={() => set_editing(false)}
            />
          </Suspense>
        )}
      </div>
    </div>
  )
})

/** 읽는 중인 줄: 본문·아래 붙는 것들·손. 고치는 중에는 그리지 않는다. */
/**
 * 손(반응 막대)은 줄 위로 조금 떠 있다. 줄이 스크롤 상자의 맨 위에 붙어 있으면 그 뜬 부분이 상자에 잘려
 * 단추 윗부분만 보인다 — 그때만 막대를 줄 안쪽으로 내린다(3차 F253). 손이 들어올 때 한 번 재고 DOM 표식만
 * 바꾼다(다시 그리지 않는다).
 */
function hands_room(row: HTMLElement) {
  let box = row.parentElement
  while (box !== null && !/auto|scroll|hidden/.test(getComputedStyle(box).overflowY)) {
    box = box.parentElement
  }
  const top = box?.getBoundingClientRect().top ?? 0
  row.toggleAttribute('data-hands-low', row.getBoundingClientRect().top - top < HANDS_ROOM)
}
/** 막대 높이(32)와 떠 있는 만큼(8)을 더한 것 — 이만큼 여유가 없으면 잘린다. */
const HANDS_ROOM = 40

/** 쓴 사람의 이름. 명단에 없으면 번호로 — 빈칸으로 두지 않는다. */
function who_of(name_of: (u: UserId) => string | undefined, u: UserId): string {
  return name_of(u) ?? `#${u}`
}

function Shown(props: MessageRowProps & { begin_edit(): void; mine: boolean }) {
  const { line, name_of, me, on_react, on_thread, on_edit, on_delete, on_action } = props
  return (
    <>
      <BodyView
        body={line.body}
        name_of={name_of}
        me={me}
        files={props.files}
        group_of={props.group_of}
        emoji_of={props.emoji_of}
        on_who={props.on_who}
        on_action={
          on_action === undefined
            ? undefined
            : (block, action, value) => on_action(line.seq, block, action, value)
        }
      />
      <Under
        line={line}
        tallies={props.tallies}
        poll={props.poll}
        me={props.me}
        reply_count={props.reply_count}
        reply_last={props.reply_last}
        cards={props.cards}
        emoji_of={props.emoji_of}
        name_of={name_of}
        {...(on_react === undefined ? {} : { on_react })}
        {...(props.on_poll_vote === undefined ? {} : { on_poll_vote: props.on_poll_vote })}
        {...(props.on_poll_unvote === undefined ? {} : { on_poll_unvote: props.on_poll_unvote })}
        {...(props.on_poll_close === undefined ? {} : { on_poll_close: props.on_poll_close })}
        {...(props.emoji_names === undefined ? {} : { emoji_names: props.emoji_names })}
        {...(props.on_hide_card === undefined || line.author !== props.me
          ? {}
          : { on_hide_card: props.on_hide_card })}
        {...(on_thread === undefined ? {} : { on_thread })}
      />
      <Suspense fallback={null}>
        <Hands
          seq={line.seq}
          mine={props.mine}
          tallies={props.tallies}
          begin_edit={props.begin_edit}
          {...(on_edit === undefined ? {} : { on_edit })}
          {...(on_delete === undefined ? {} : { on_delete })}
          {...(on_react === undefined ? {} : { on_react })}
          {...(on_thread === undefined ? {} : { on_thread })}
          {...(props.on_pin === undefined ? {} : { on_pin: props.on_pin })}
          {...(props.on_save === undefined ? {} : { on_save: props.on_save })}
          {...(props.on_remind === undefined ? {} : { on_remind: props.on_remind })}
          {...(props.on_unread === undefined ? {} : { on_unread: props.on_unread })}
          {...(props.on_link === undefined ? {} : { on_link: props.on_link })}
          {...(props.on_forward === undefined ? {} : { on_forward: props.on_forward })}
          {...(props.shortcuts === undefined ? {} : { shortcuts: props.shortcuts })}
          {...(props.on_shortcut === undefined ? {} : { on_shortcut: props.on_shortcut })}
          {...(props.emoji_names === undefined ? {} : { emoji_names: props.emoji_names })}
          emoji_of={props.emoji_of}
          pinned={props.pinned === true}
          saved={props.saved === true}
        />
      </Suspense>
    </>
  )
}

/** 머리: 이름과 시각. 이름을 누를 수 있으면 단추, 아니면 글자다. */
function Head({
  name,
  bot,
  written_at,
  press,
}: {
  name: string
  bot: boolean
  written_at: string
  press?(): void
}) {
  const t = useT()
  const locale = useLocale()
  return (
    <div className="head">
      {press === undefined ? (
        <span className="who">{name}</span>
      ) : (
        <Button variant="link" size="sm" className="who" onClick={press}>
          {name}
        </Button>
      )}
      {bot && (
        <Badge className="bot-mark" variant="outline">
          {t('봇')}
        </Badge>
      )}
      <time dateTime={written_at}>{clock(written_at, locale)}</time>
    </div>
  )
}

interface UnderProps extends RowActions {
  line: Line
  tallies: readonly Tally[] | undefined
  poll: PollLook | null | undefined
  me: UserId
  reply_count: number | undefined
  reply_last?: string | undefined
  cards?: CardMap | undefined
  emoji_of?: EmojiLook | undefined
  name_of(user: UserId): string | undefined
}

/** 본문 아래: 수정됨 표시, 반응 알약, 답글 수. */
function Under(props: UnderProps) {
  const { line, tallies, reply_count, cards, on_react, on_thread, emoji_of, name_of } = props
  const { on_hide_card } = props
  const t = useT()
  const locale = useLocale()
  return (
    <>
      {cards !== undefined && (
        <LinkCards
          urls={links_of(line.body)}
          cards={cards}
          on_hide={on_hide_card === undefined ? undefined : (url) => on_hide_card(line.seq, url)}
        />
      )}
      {(line.edited_at !== null || line.broadcast === true) && (
        <div className="notes">
          {line.edited_at !== null && <span className="edited">{t('(편집됨)')}</span>}
          {line.broadcast === true &&
            // 본류에서는 누르면 그 스레드가 열리고, 스레드 판 안(`on_thread` 없음)에서는 채널에도 갔다는 말만.
            (on_thread !== undefined && line.parent != null ? (
              <button
                type="button"
                className="replied"
                onClick={() => line.parent != null && on_thread(line.parent)}
              >
                <MessagesSquare size={12} aria-hidden />
                {t('스레드에 단 답글')}
              </button>
            ) : (
              <span className="replied">
                <MessagesSquare size={12} aria-hidden />
                {t('채널에도 보냄')}
              </span>
            ))}
        </div>
      )}
      {props.poll != null && (
        <Suspense fallback={null}>
          <PollCard
            seq={line.seq}
            poll={props.poll}
            me={props.me}
            name_of={name_of}
            {...(props.on_poll_vote === undefined ? {} : { on_vote: props.on_poll_vote })}
            {...(props.on_poll_unvote === undefined ? {} : { on_unvote: props.on_poll_unvote })}
            {...(props.on_poll_close === undefined ? {} : { on_close: props.on_poll_close })}
          />
        </Suspense>
      )}
      {on_react !== undefined && tallies !== undefined && (
        <ReactionBar
          seq={line.seq}
          tallies={tallies}
          on_toggle={on_react}
          emoji_of={emoji_of}
          name_of={name_of}
          more={
            <Suspense fallback={null}>
              <ReactPick
                look="pill"
                seq={line.seq}
                on_toggle={on_react}
                tallies={tallies}
                emoji_of={emoji_of}
                emoji_names={props.emoji_names}
              />
            </Suspense>
          }
        />
      )}
      {on_thread !== undefined && (reply_count ?? 0) > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="replies"
          icon={<MessagesSquare size={14} aria-hidden />}
          onClick={() => on_thread(line.seq)}
        >
          {t('답글 {n}', { n: reply_count ?? 0 })}
          {props.reply_last !== undefined && (
            <time
              className="last-reply"
              dateTime={props.reply_last}
              title={fmt_time(props.reply_last, locale)}
            >
              {t('마지막 답글 {when}', { when: fmt_relative(props.reply_last, locale) })}
            </time>
          )}
        </Button>
      )}
    </>
  )
}

export interface HandsProps extends RowActions {
  seq: Seq
  mine: boolean
  tallies?: readonly Tally[] | undefined
  emoji_of?: EmojiLook | undefined
  begin_edit(): void
  pinned: boolean
  saved: boolean
}

/** `오후 3:05`. 사람의 시간대로 그린다 — 서버 시각을 그대로 보이면 아무도 못 읽는다. */
function clock(iso: string, locale: Locale): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  return fmt_time(at, locale)
}

/** 줄에서 오른쪽 클릭했을 때 우리 메뉴를 열까. 고른 글자가 있거나 링크·입력칸·단추 위면 브라우저 것이다. */
export function row_menu_wanted(target: EventTarget | null, selected: string): boolean {
  if (selected.trim() !== '') return false
  const el = target as { closest?: (sel: string) => unknown } | null
  if (typeof el?.closest !== 'function') return true
  return (
    el.closest('a, input, textarea, button, video, audio, img, [contenteditable="true"]') === null
  )
}
